import { useMemo } from 'react';
import { ButtonId, Milestone, OrderStage, ButtonState } from '../types/button_stage';
import { BUTTON_RULES } from '../utils/stageconfig';

export const STAGE_FLOW: OrderStage[] = [
    "Order Created",
    "Order Accepted",
    "Order Rejected",
    "Documents",
    "Payments",
    "Delivery",
    "Order Closed",
];


// Helper functions
const getCurrentStage = (milestones: Milestone[]): OrderStage | null => {
    const activeMilestone = milestones?.find((m) => m.status === "Active");
    return activeMilestone ? activeMilestone.stage : null;
};

const getCompletedStages = (milestones: Milestone[]): OrderStage[] => {
    return milestones?.filter((m) => m.status === "Fulfilled").map((m) => m.stage);
};

const isOrderCancelledOrClosed = (milestones: Milestone[]): boolean => {
    const currentStage = getCurrentStage(milestones);
    return currentStage === "Order Closed" || currentStage === "Order Rejected";
};

const getLiveStage = (milestones: Milestone[]): { stage: string; sub_stage: string } | null => {
    const liveMilestone = milestones?.find((m) => m.status === "Active");
    const isCancelled = milestones?.find((m) => m.status === 'Cancelled');
    
    if (isCancelled) {
        return {
            stage: isCancelled?.stage as string,
            sub_stage: isCancelled?.stage as string,
        };
    }
    
    const liveStage = liveMilestone?.sub_stages;
    if (Array.isArray(liveStage) && liveStage.length > 0) {
        return {
            stage: liveMilestone?.stage as string,
            sub_stage: liveStage?.at(-1)?.sub_stage_name as string
        };
    } else if (liveMilestone?.stage) {
        return {
            stage: '',
            sub_stage: liveMilestone?.stage as string,
        };
    }
    
    const orderCancelledOrClosed = isOrderCancelledOrClosed(milestones);
    const sortedMilestones = [...(milestones || [])]
        .sort((a, b) => a.priority - b.priority);
        
    if (orderCancelledOrClosed) {
        return {
            stage: sortedMilestones?.at(-1)?.stage as string,
            sub_stage: sortedMilestones?.at(-1)?.sub_stages?.at(-1)?.sub_stage_name as string
        };
    }
    
    const miles = sortedMilestones?.filter((m) => m.status === "Fulfilled")?.at(-1);
    const stage = miles?.stage;
    const last_sub_stage = miles?.sub_stages;
    
    if (Array.isArray(last_sub_stage) && last_sub_stage.length > 0) {
        return {
            stage: stage as string,
            sub_stage: last_sub_stage?.at(-1)?.sub_stage_name as string
        };
    }
    
    return {
        stage: stage as string,
        sub_stage: stage as string
    };
};

// Custom hook
export const useOrderStageStatus = (milestones: Milestone[] = []) => {
    const stageInfo = useMemo(() => {
        const currentStage = getCurrentStage(milestones);
        const completedStages = getCompletedStages(milestones);
        const liveStage = getLiveStage(milestones);
        const isCancelledOrClosed = isOrderCancelledOrClosed(milestones);
        
        return {
            currentStage,
            completedStages,
            liveStage,
            isCancelledOrClosed,
        };
    }, [milestones]);

    const getButtonState = useMemo(() => {
        return (buttonId: ButtonId): ButtonState => {
            const rule = BUTTON_RULES[buttonId];
            return {
                disabled: rule.disabled(milestones),
                visible: rule.visible(milestones),
            };
        };
    }, [milestones]);

    const getAllButtonStates = useMemo(() => {
        const states: Record<ButtonId, ButtonState> = {} as Record<ButtonId, ButtonState>;
        
        (Object.keys(BUTTON_RULES) as ButtonId[]).forEach((buttonId) => {
            states[buttonId] = getButtonState(buttonId);
        });
        
        return states;
    }, [getButtonState]);

    const isButtonDisabled = useMemo(() => {
        return (buttonId: ButtonId): boolean => {
            return getButtonState(buttonId).disabled;
        };
    }, [getButtonState]);

    const isButtonVisible = useMemo(() => {
        return (buttonId: ButtonId): boolean => {
            return getButtonState(buttonId).visible;
        };
    }, [getButtonState]);

    const canProceedToStage = useMemo(() => {
        return (targetStage: OrderStage): boolean => {
            const { completedStages, currentStage } = stageInfo;
            const targetIndex = STAGE_FLOW.indexOf(targetStage);
            const currentIndex = currentStage ? STAGE_FLOW.indexOf(currentStage) : -1;
            
            // Check if all previous stages are completed
            for (let i = 0; i < targetIndex; i++) {
                if (!completedStages.includes(STAGE_FLOW[i])) {
                    return false;
                }
            }
            
            return currentIndex === targetIndex || currentIndex < targetIndex;
        };
    }, [stageInfo]);

    return {
        // Stage information
        ...stageInfo,
        
        // Button state methods
        getButtonState,
        getAllButtonStates,
        isButtonDisabled,
        isButtonVisible,
        
        // Stage progression helpers
        canProceedToStage,
        stageFlow: STAGE_FLOW,
        
        // Raw milestones for reference
        milestones,
    };
};

export default useOrderStageStatus;